import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadImage } from '../../utils/cloudinary';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  gray: '#64748b',
  lightGray: '#f1f5f9',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

// Premium styled components
const Form = styled.form`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${COLORS.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${COLORS.primary};
  margin-bottom: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #1e40af;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  width: 100%;
  max-width: 300px;
  position: relative;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 4px;
  border: 1px solid #d1d5db;
`;

const HeroList = styled.div`
  margin-top: 2rem;
`;

const HeroItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const HeroImageThumb = styled.img`
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 1rem;
`;

const HeroDetails = styled.div`
  flex: 1;
`;

const HeroTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 0.25rem 0;
  color: #1f2937;
`;

const HeroSubtitle = styled.p`
  font-size: 0.9rem;
  margin: 0;
  color: #6b7280;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const RemoveButton = styled(Button)`
  padding: 0.5rem 1rem;
  background-color: #ef4444;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-left: 10px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => props.type === 'error' ? '#fee2e2' : '#d1fae5'};
  color: ${props => props.type === 'error' ? '#b91c1c' : '#065f46'};
`;

const HeroManagement = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [title, setTitle] = useState('Welcome to Lakeside Haven');
  const [subtitle, setSubtitle] = useState('Experience luxury and tranquility on the shores of Wular Lake');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchHeroContent();
  }, []);

  const fetchHeroContent = async () => {
    try {
      setLoading(true);
      const heroCollection = collection(db, 'heroContent');
      const snapshot = await getDocs(heroCollection);
      
      const heroData = [];
      snapshot.forEach((doc) => {
        heroData.push({ id: doc.id, ...doc.data() });
      });
      
      setHeroImages(heroData);
      
      // If there's hero text content, use the first one
      const textContent = heroData.find(item => item.type === 'text');
      if (textContent) {
        setTitle(textContent.title || '');
        setSubtitle(textContent.subtitle || '');
        setEditingId(textContent.id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hero content:', error);
      setMessage({ text: 'Failed to load hero content', type: 'error' });
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      let imageUrl = '';
      
      // Upload image if provided
      if (imageFile) {
        const uploadResult = await uploadImage(imageFile);
        imageUrl = uploadResult.url;
      }
      
      // Save to Firebase
      if (imageUrl) {
        await addDoc(collection(db, 'heroContent'), {
          type: 'image',
          imageUrl,
          createdAt: new Date(),
        });
        
        setMessage({ text: 'Hero image added successfully', type: 'success' });
        setImageFile(null);
        setImagePreview('');
      }
      
      // Update or add text content
      if (title || subtitle) {
        if (editingId) {
          // Update existing text content
          await updateDoc(doc(db, 'heroContent', editingId), {
            type: 'text',
            title,
            subtitle,
            updatedAt: new Date(),
          });
        } else {
          // Add new text content
          const docRef = await addDoc(collection(db, 'heroContent'), {
            type: 'text',
            title,
            subtitle,
            createdAt: new Date(),
          });
          setEditingId(docRef.id);
        }
        
        setMessage({ text: 'Hero text updated successfully', type: 'success' });
      }
      
      // Refresh the hero content
      fetchHeroContent();
    } catch (error) {
      console.error('Error saving hero content:', error);
      setMessage({ text: 'Failed to save hero content', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'heroContent', id));
        setMessage({ text: 'Item deleted successfully', type: 'success' });
        fetchHeroContent();
      } catch (error) {
        console.error('Error deleting item:', error);
        setMessage({ text: 'Failed to delete item', type: 'error' });
        setLoading(false);
      }
    }
  };

  return (
    <>
      {message.text && (
        <Message type={message.type}>{message.text}</Message>
      )}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Hero Title</Label>
          <Input 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter hero title"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Hero Subtitle</Label>
          <TextArea 
            value={subtitle} 
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Enter hero subtitle"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Add Hero Image</Label>
          <Input 
            type="file" 
            accept="image/*" 
            onChange={handleImageChange}
          />
          {imagePreview && (
            <ImagePreview>
              <PreviewImage src={imagePreview} alt="Preview" />
            </ImagePreview>
          )}
        </FormGroup>
        
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
          {loading && <LoadingSpinner />}
        </Button>
      </Form>
      
      <HeroList>
        <Title>Hero Images</Title>
        {heroImages.filter(item => item.type === 'image').map((item) => (
          <HeroItem key={item.id}>
            <HeroImageThumb src={item.imageUrl} alt="Hero" />
            <HeroDetails>
              <HeroTitle>Hero Image</HeroTitle>
              <HeroSubtitle>Added on {new Date(item.createdAt.seconds * 1000).toLocaleDateString()}</HeroSubtitle>
            </HeroDetails>
            <ActionButtons>
              <RemoveButton onClick={() => handleDelete(item.id)}>Remove</RemoveButton>
            </ActionButtons>
          </HeroItem>
        ))}
        
        {heroImages.filter(item => item.type === 'image').length === 0 && (
          <p>No hero images added yet.</p>
        )}
      </HeroList>
    </>
  );
};

export default HeroManagement;
